import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { FormComponent } from '../form/form.component';
import { SurveyResultsComponent } from '../intake/survey-results/survey-results.component';
import { IntakeService } from '../services/intake.service';
import { SurveyRunStateService } from '../services/survey-run-state.service';
import type { SurveyForIntakeDto, SubmitSurveyRequestDto, SubmitSurveyResponseDto } from '../models/intake-dto';

/** postMessage payload so a host page can scroll the iframe into view after submit. */
export const SURVEY_SCROLL_TO_RESULTS_MESSAGE = {
  source: 'ai-scorecard-survey',
  action: 'scroll-to-results'
} as const;

@Component({
  selector: 'app-survey-page',
  standalone: true,
  imports: [CommonModule, FormComponent, SurveyResultsComponent],
  template: `
    <div class="survey-container" #resultsAnchor>
      @if (loading) {
        <p role="status" aria-live="polite">Loading osaf_survey...</p>
      }
      @if (errorMessage) {
        <p class="survey-form__error" role="alert">{{ errorMessage }}</p>
      }
      @if (survey && !showResultsView && !loading) {
        <app-form
          [survey]="survey"
          [token]="token"
          [runId]="runId"
          [isPreview]="false"
          [submitInProgress]="submitInProgress"
          [submitError]="errorMessage"
          (submitted)="onSubmit($event)"
        />
      }
      @if (showResultsView && !loading) {
        <app-survey-results
          [result]="submittedResult"
          [evaluating]="submitInProgress"
          [runId]="runId"
          [token]="token"
          [collectContactInfo]="survey?.collectContactInfo ?? false"
        />
      }
    </div>
  `,
  styles: [':host { display: block; }']
})
export class SurveyPageComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('resultsAnchor') resultsAnchor?: ElementRef<HTMLElement>;

  survey: SurveyForIntakeDto | null = null;
  token = '';
  runId = 0;
  loading = true;
  errorMessage: string | null = null;
  submittedResult: SubmitSurveyResponseDto | null = null;
  submitInProgress = false;

  /** True when we should show the results view (either evaluating or result received). */
  get showResultsView(): boolean {
    return this.submitInProgress || this.submittedResult != null;
  }

  private readonly destroy$ = new Subject<void>();
  private pendingScrollToResults = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly intake: IntakeService,
    private readonly runState: SurveyRunStateService
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          const id = params.get('surveyRunId');
          this.runId = id ? parseInt(id, 10) : 0;
          this.token = this.runState.getToken() ?? '';
          if (!this.runId || !this.token) {
            this.loading = false;
            this.errorMessage = 'Invalid or expired session. Please start the survey again.';
            return [];
          }
          this.loading = true;
          this.errorMessage = null;
          return this.intake.getSurvey(this.runId, this.token);
        })
      )
      .subscribe({
        next: (s) => {
          this.survey = s;
          this.token = s.token;
          this.loading = false;
        },
        error: (err: Error) => {
          this.loading = false;
          this.errorMessage = err?.message ?? 'Failed to load osaf_survey.';
        }
      });
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScrollToResults || !this.showResultsView || !this.resultsAnchor) return;
    this.pendingScrollToResults = false;
    this.scrollToResults();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(request: SubmitSurveyRequestDto): void {
    if (!this.survey) return;
    this.submitInProgress = true;
    this.errorMessage = null;
    this.pendingScrollToResults = true;
    this.intake
      .submit(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.submittedResult = res;
          this.submitInProgress = false;
          // Results content grew (score revealed); nudge host/iframe scroll again.
          this.pendingScrollToResults = true;
        },
        error: (err: Error) => {
          this.errorMessage = err?.message ?? 'Submission failed.';
          this.submitInProgress = false;
        }
      });
  }

  /**
   * Bring results into view after submit.
   * - Scrolls this document (helps when the iframe itself scrolls).
   * - postMessages the parent so bonadio.com (or any host) can scroll the page/iframe into view.
   *   Cross-origin parents cannot be scrolled from here without that listener.
   */
  private scrollToResults(): void {
    const el = this.resultsAnchor?.nativeElement;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }

    try {
      window.parent.postMessage({ ...SURVEY_SCROLL_TO_RESULTS_MESSAGE }, '*');
    } catch {
      /* ignore */
    }

    // Same-origin embeds only: scroll parent so the iframe top is visible.
    try {
      if (window.parent !== window.self) {
        const frames = window.parent.document.getElementsByTagName('iframe');
        for (let i = 0; i < frames.length; i++) {
          if (frames[i].contentWindow === window) {
            frames[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          }
        }
      }
    } catch {
      /* cross-origin — rely on postMessage */
    }
  }
}
